import {
  Badge,
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  HStack,
} from '@chakra-ui/react';
import React, { useState, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Ellipsis, Wallet, SquarePen, FileText } from 'lucide-react';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import {
  ProviderCausePayoutStatusSchema,
  type ProviderCausePayoutStatus,
} from '@duke-hq/svc-api-db/schemas/enums/ProviderCausePayoutStatus.schema';
import {
  ProviderCausePayoutTriggerKindSchema,
  type ProviderCausePayoutTriggerKind,
} from '@duke-hq/svc-api-db/schemas/enums/ProviderCausePayoutTriggerKind.schema';
import { SortOrder } from '@duke-hq/lib/pagination';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { ProviderCausesModal } from './ProviderCausesModal';
import type { SortFilters } from './ProviderCausesLayout';

const getStatusColorScheme = (status: ProviderCausePayoutStatus) => {
  switch (status) {
    case ProviderCausePayoutStatusSchema.enum.PAID:
      return 'green';
    case ProviderCausePayoutStatusSchema.enum.PENDING:
      return 'yellow';
    case ProviderCausePayoutStatusSchema.enum.FAILED:
      return 'red';
    case ProviderCausePayoutStatusSchema.enum.CANCELLED:
      return 'gray';
    default:
      return 'gray';
  }
};

const getTriggerKindColorScheme = (triggerKind: ProviderCausePayoutTriggerKind) => {
  switch (triggerKind) {
    case ProviderCausePayoutTriggerKindSchema.enum.TARGET_REACHED:
      return 'blue';
    case ProviderCausePayoutTriggerKindSchema.enum.TIMEFRAME_REACHED:
      return 'purple';
    case ProviderCausePayoutTriggerKindSchema.enum.MANUAL:
      return 'orange';
    default:
      return 'gray';
  }
};

const formatTriggerKind = (triggerKind: ProviderCausePayoutTriggerKind) => {
  return triggerKind.replace(/_/g, ' ');
};

const truncateText = (text: string, maxLength: number = 50) => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + '...';
};

const NotesCell: React.FC<{ notes: string | null }> = ({ notes }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!notes) {
    return <Text>-</Text>;
  }

  return (
    <>
      <HStack spacing={2}>
        <Text>{truncateText(notes, 50)}</Text>
        {notes.length > 50 && (
          <IconButton
            aria-label="View full notes"
            icon={<FileText width="16px" height="16px" />}
            size="xs"
            variant="ghost"
            onClick={onOpen}
          />
        )}
      </HStack>

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent p={4}>
          <ModalHeader>
            <Trans>Notes</Trans>
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody pb={6}>
            <Text whiteSpace="pre-wrap">{notes}</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

type ProviderCauseTableProps = {
  causes?: RouterOutputs['finances']['providerCausesReport']['listProviderCauses']['items'];
  getProviderName: (providerDrn: string) => string;
  urlParams: SortFilters;
  onFilters: (filters: SortFilters) => void;
};

const ProviderCauseRow: React.FC<{
  cause: RouterOutputs['finances']['providerCausesReport']['listProviderCauses']['items'][number];
  getProviderName: (providerDrn: string) => string;
  onOpenPaymentModal: () => void;
}> = React.memo(({ cause, getProviderName, onOpenPaymentModal }) => {
  return (
    <Tr _hover={{ bg: 'bg.subtle' }}>
      <Td>{cause.cause?.name ?? 'Unknown Cause'}</Td>
      <Td>
        <Text>{getProviderName(cause.providerDrn)}</Text>
      </Td>
      <Td>{cause.totalPointsContributed}</Td>
      <Td>${cause.payoutAmount?.toString() ?? '0'}</Td>
      <Td>
        <Badge
          colorScheme={getTriggerKindColorScheme(cause.triggerKind)}
          variant="outline"
          borderRadius="md"
          borderWidth="1px"
        >
          {formatTriggerKind(cause.triggerKind)}
        </Badge>
      </Td>
      <Td>{cause.triggeredAt ? new Date(cause.triggeredAt).toLocaleDateString() : '-'}</Td>
      <Td>{cause.paidAt ? new Date(cause.paidAt).toLocaleDateString() : '-'}</Td>
      <Td>
        <Badge colorScheme={getStatusColorScheme(cause.status)} variant="subtle">
          {cause.status}
        </Badge>
      </Td>
      <Td>
        <NotesCell notes={cause.notes} />
      </Td>
      <Td position="sticky" right={0} zIndex={1} bg="bg.surface" px={8}>
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          boxShadow="-4px 0px 6px rgba(0, 0, 0, 0.1)"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<Ellipsis width="20px" height="20px" />}
              variant="ghost"
              size="sm"
              onClick={(e) => e.stopPropagation()}
              display="inline-flex"
            />
            <Portal>
              <MenuList>
                <MenuItem
                  icon={<Wallet width={16} height={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenPaymentModal();
                  }}
                >
                  <Trans>Update payment</Trans>
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Box>
      </Td>
    </Tr>
  );
});

ProviderCauseRow.displayName = 'ProviderCauseRow';

export function ProviderCauseTable({
  causes = [],
  getProviderName,
  urlParams,
  onFilters,
}: ProviderCauseTableProps) {
  const { sort, orderBy } = urlParams;

  const handleSort = useCallback(
    (orderBy: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      onFilters({ ...urlParams, sort: newSortDirection, orderBy });
    },
    [onFilters, sort, urlParams],
  );

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<
    RouterOutputs['finances']['providerCausesReport']['listProviderCauses']['items'][number] | null
  >(null);

  return (
    <>
      <Table>
        <Thead>
          <Tr>
            <Th>
              <SortableHeader
                label={t`Name`}
                columnName="name"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <Trans>Provider</Trans>
            </Th>
            <Th>
              <Trans>Points Contributed</Trans>
            </Th>
            <Th>
              <Trans>Amount</Trans>
            </Th>
            <Th>
              <Trans>Kind</Trans>
            </Th>
            <Th>
              <Trans>Triggered At</Trans>
            </Th>
            <Th>
              <Trans>Paid At</Trans>
            </Th>
            <Th>
              <Trans>Status</Trans>
            </Th>
            <Th>
              <Trans>Notes</Trans>
            </Th>
            <Th position="sticky" right={0} zIndex={2} bg="white !important" px={8}>
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                boxShadow="-4px 0px 6px rgba(0, 0, 0, 0.1)"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <SquarePen width={20} height={20} />
              </Box>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {Array.isArray(causes) && causes.length
            ? causes.map((cause) => (
                <ProviderCauseRow
                  key={cause.id}
                  cause={cause}
                  getProviderName={getProviderName}
                  onOpenPaymentModal={() => {
                    setIsPaymentModalOpen(cause);
                  }}
                />
              ))
            : null}
        </Tbody>
      </Table>

      {isPaymentModalOpen != null ? (
        <ProviderCausesModal
          cause={isPaymentModalOpen}
          isOpen={!!isPaymentModalOpen}
          onClose={() => {
            setIsPaymentModalOpen(null);
          }}
        />
      ) : null}
    </>
  );
}
